import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography, TextField, IconButton, Chip, Tooltip, Button } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import EastIcon from '@mui/icons-material/East';
import { useStoreOperationEdits } from '../hooks/useStoreOperationEdits';
import { getOperationConflictKey } from '../utils/operationIdentity';
import {
  CurrentNamedPrice,
  formatNamedPriceAmount,
  formatCents,
  isUnpricedNamedPrice,
  parseCentsInput,
} from '../utils/namedPriceUtils';

interface NamedPriceEditorProps {
  patchId: string;
  storeNumber: string;
  /** The store's existing overrides, from its loaded patch. */
  currentNamedPrices: CurrentNamedPrice[];
  /** The org catalog, so a price the store has not set yet can still be added. */
  catalog?: Array<{ namedPriceId: string; name: string }>;
  /** Editing is gated by the caller; read-only keeps the previous display exactly as it was. */
  editable?: boolean;
}

interface NamedPriceRow extends CurrentNamedPrice {
  /** True when the patch has no override for this price yet - a value here creates one. */
  isNew: boolean;
}

/**
 * A store's named prices, editable in place.
 *
 * Named prices are not part of the menu tree, so a store's patch is the only place its value is visible
 * - which makes this list the natural place to change it, rather than a separate form. Amounts are whole
 * cents, matching the rest of the bulk-update forms.
 */
export const NamedPriceEditor: React.FC<NamedPriceEditorProps> = ({
  patchId,
  storeNumber,
  currentNamedPrices,
  catalog = [],
  editable = false,
}) => {
  const { stateFor, displayedAmountFor, setAmount, removeOperation, undo } = useStoreOperationEdits({
    patchId,
    storeNumber,
  });

  /** Amount fields being typed in, keyed by namedPriceId. Committed on blur or Enter. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const rows = useMemo<NamedPriceRow[]>(() => {
    const existing = currentNamedPrices.map((namedPrice) => ({ ...namedPrice, isNew: false }));

    if (!editable) return existing;

    // Catalog entries the store has no override for, so a price can be added here too.
    const overridden = new Set(existing.map((namedPrice) => namedPrice.namedPriceId));
    const addable = catalog
      .filter((namedPrice) => !overridden.has(namedPrice.namedPriceId))
      .map((namedPrice) => ({
        namedPriceId: namedPrice.namedPriceId,
        name: namedPrice.name,
        amount: undefined,
        isNew: true,
      }));

    return [...existing, ...addable].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [currentNamedPrices, catalog, editable]);

  /**
   * The node shape a named price operation has on a patch. Built rather than carried through, so a
   * catalog entry with no operation yet goes down the same path as an existing override.
   */
  const nodeFor = useCallback(
    (row: NamedPriceRow) => ({
      __typename: 'SetNamedPriceAmountPatchOperation',
      namedPrice: { namedPriceId: row.namedPriceId, name: row.name },
      ...(row.amount !== undefined && { amount: row.amount }),
    }),
    []
  );

  const commit = useCallback(
    (row: NamedPriceRow, raw: string) => {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[row.namedPriceId];
        return next;
      });

      const node = nodeFor(row);
      const amount = parseCentsInput(raw);
      // Unparseable or unchanged: discard and let the field revert.
      if (amount === null || amount === displayedAmountFor(node, row.amount)) return;

      setAmount(node, amount, row.amount);
    },
    [displayedAmountFor, nodeFor, setAmount]
  );

  if (rows.length === 0) return null;

  return (
    <Box sx={{ py: 1.5 }}>
      {/* h6 is 12px bold in this theme - matches the body2 rows below it */}
      <Typography variant='h6' color='textSecondary' sx={{ mb: 1 }}>
        Named Prices
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          columnGap: 4,
        }}
      >
        {rows.map((row) => {
          const node = nodeFor(row);
          const conflictKey = getOperationConflictKey(node);
          const state = stateFor(conflictKey);
          const displayed = displayedAmountFor(node, row.amount);
          const draft = drafts[row.namedPriceId];
          const isDeleted = state === 'deleted';
          const isEdited = state === 'edited';

          return (
            <Box
              key={row.namedPriceId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: isDeleted ? 0.6 : 1,
              }}
              data-testid={`namedPriceRow-${row.namedPriceId}`}
            >
              <Typography
                variant='body2'
                sx={{ textDecoration: isDeleted ? 'line-through' : undefined, minWidth: 0 }}
                noWrap
                title={row.name}
              >
                {row.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {/* The value being replaced, so a change is legible without opening anything */}
                {isEdited && !isUnpricedNamedPrice(row.amount) && (
                  <>
                    <Typography
                      variant='body2'
                      color='textSecondary'
                      sx={{ textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatCents(row.amount as number)}
                    </Typography>
                    <EastIcon fontSize='small' color='disabled' />
                  </>
                )}

                {editable && !isDeleted ? (
                  <TextField
                    size='small'
                    variant='standard'
                    placeholder={row.isNew ? 'Not set' : undefined}
                    value={draft ?? (displayed === undefined ? '' : String(displayed))}
                    onChange={(event) =>
                      setDrafts((prev) => ({ ...prev, [row.namedPriceId]: event.target.value }))
                    }
                    onBlur={(event) => commit(row, event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      style: { textAlign: 'right', width: 70 },
                      'aria-label': `Amount in cents for ${row.name}`,
                    }}
                  />
                ) : (
                  <Typography
                    variant='body2'
                    color={displayed === undefined ? 'textSecondary' : undefined}
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      textDecoration: isDeleted ? 'line-through' : undefined,
                    }}
                  >
                    {formatNamedPriceAmount(displayed)}
                  </Typography>
                )}

                {editable && (
                  <>
                    {isEdited && (
                      <Chip
                        size='small'
                        color='primary'
                        variant='outlined'
                        label='Pending'
                        sx={{ fontSize: '0.6rem', height: '16px', '& .MuiChip-label': { px: 0.5 } }}
                      />
                    )}
                    {isDeleted && (
                      <Chip
                        size='small'
                        color='error'
                        variant='outlined'
                        label='Removing'
                        sx={{ fontSize: '0.6rem', height: '16px', '& .MuiChip-label': { px: 0.5 } }}
                      />
                    )}
                    {/* Only an override can be removed - there is nothing to remove from the patch for a
                        catalog entry the store has never priced. */}
                    {!row.isNew && !isDeleted && (
                      <Tooltip title={`Remove this store's ${row.name} price`}>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => removeOperation(node, row.amount)}
                          aria-label={`Remove ${row.name}`}
                        >
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                    {state !== 'clean' && (
                      <Button size='small' startIcon={<UndoIcon fontSize='small' />} onClick={() => undo(conflictKey)}>
                        Undo
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default NamedPriceEditor;
